/**
 * Triage service — reads pending email-triage items and commits/dismisses them
 * against the beta API.
 *
 * Falls back to a static mock list when the triage API isn't reachable (e.g. a
 * local `npm start` before the triage backend is deployed), mirroring the
 * fleetService -> STATIC_FLEET fallback. This lets the triage section render and
 * be exercised locally before beta. `isMock` is surfaced so the UI can show a
 * "preview data" banner.
 */
import { UnscheduledEvent } from '../types';
import { createLogger } from '../utils/logger';
import { splitCompoundDmiDefectList } from '../utils/triage-defects';
import { authedFetch } from './apiClient';
import { MOCK_TRIAGE } from '../data/mockTriage';

const logger = createLogger('TriageService');

interface TriageDateTime {
  date?: string;
  time?: string;
}

/** Describes whether an inbound Triage record has an ERTS change to apply. */
export type TriageErtsAction = 'none' | 'set' | 'remove';

/** Adds the triage buffer to a complete auto-ingested UTC date/time pair. */
function addTriageTimeBuffer(date: string | undefined, time: string | undefined): TriageDateTime {
  const dateMatch: RegExpMatchArray | null = date?.match(/^(\d{4})-(\d{2})-(\d{2})$/) || null;
  const timeMatch: RegExpMatchArray | null = time?.match(/^(\d{2}):(\d{2})$/) || null;
  if (!dateMatch || !timeMatch) return { date, time };
  const bufferedDate: Date = new Date(
    Date.UTC(
      Number(dateMatch[1]),
      Number(dateMatch[2]) - 1,
      Number(dateMatch[3]),
      Number(timeMatch[1]),
      Number(timeMatch[2]) + 30,
    ),
  );
  return {
    date: `${String(bufferedDate.getUTCFullYear()).padStart(4, '0')}-${String(bufferedDate.getUTCMonth() + 1).padStart(2, '0')}-${String(bufferedDate.getUTCDate()).padStart(2, '0')}`,
    time: `${String(bufferedDate.getUTCHours()).padStart(2, '0')}:${String(bufferedDate.getUTCMinutes()).padStart(2, '0')}`,
  };
}

/** Normalizes untrusted API action values and retains legacy populated ERTS rows. */
function normalizeErtsAction(
  action: unknown,
  ertsDate: string | undefined,
  ertsTime: string | undefined,
): TriageErtsAction {
  if (action === 'remove') return 'remove';
  if (action === 'set') return 'set';
  return ertsDate || ertsTime ? 'set' : 'none';
}

/** Applies client-side compatibility transforms until every API stage serves the new contract. */
function normalizeTriageItem(item: TriageItem): TriageItem {
  const noteSuppressed: boolean = item.noteSuppressed === true;
  const sourceDefects: string[] =
    item.parsedDefects && item.parsedDefects.length > 0
      ? item.parsedDefects
      : item.discrepancy
        ? [item.discrepancy]
        : [];
  const parsedDefects: string[] = splitCompoundDmiDefectList(sourceDefects);
  const nextAdvise: TriageDateTime = item.triageTimingBufferApplied
    ? { date: item.nextAdviseDate, time: item.nextAdviseTimeZulu }
    : addTriageTimeBuffer(item.nextAdviseDate, item.nextAdviseTimeZulu);
  const erts: TriageDateTime = item.triageTimingBufferApplied
    ? { date: item.ertsDate, time: item.ertsTimeZulu }
    : addTriageTimeBuffer(item.ertsDate, item.ertsTimeZulu);
  return {
    ...item,
    noteSuppressed,
    note: noteSuppressed ? undefined : item.note,
    noteDate: noteSuppressed ? undefined : item.noteDate,
    noteTime: noteSuppressed ? undefined : item.noteTime,
    parsedDefects,
    nextAdviseDate: nextAdvise.date,
    nextAdviseTimeZulu: nextAdvise.time,
    ertsDate: erts.date,
    ertsTimeZulu: erts.time,
    ertsAction: normalizeErtsAction(item.ertsAction, erts.date, erts.time),
    triageTimingBufferApplied: true,
  };
}

/** API response for the pending-triage read endpoint. */
interface PendingTriageApiResponse {
  triageItems?: TriageItem[];
}

/** A candidate existing event a triage item could update. */
export interface TriageMatchCandidate {
  eventId: string;
  recordType: string;
  status: string;
  discrepancy: string;
  station: string;
}

/** Deferred-specific fields extracted from an ATN-Deferred email. */
export interface TriageDeferredFields {
  timeDeclaredZulu: string;
  dueDateZulu: string;
  deferralReference: string;
  catType: string;
  trk: boolean;
  apuDeferral: boolean;
  etopsRestricted: boolean;
  catIiiRestricted: boolean;
  operationallyRestrictive: boolean;
}

/** A parsed inbound email awaiting reviewer action. */
export interface TriageItem {
  triage_id: string;
  status: string;
  sourceProfileId: string | null;
  sourceEmail: string;
  senderVerified: boolean;
  targetRecordType: string | null;
  suggestedAction: 'CREATE' | 'UPDATE' | null;
  tailNumber: string | null;
  tailInFleet: boolean;
  carrier: string;
  aircraftType: string;
  subject: string;
  receivedAt: string;
  parseStatus: string;
  parseWarnings: string[];
  matchedEventId: string | null;
  matchCandidates: TriageMatchCandidate[];
  /** Body-extracted fields (sender-specific). Prefill the review modal. */
  note?: string;
  /** Source explicitly suppressed note content and its timestamp. */
  noteSuppressed?: boolean;
  noteDate?: string;
  noteTime?: string;
  /** Candidate defects recognized from the email; reviewers can edit the list. */
  parsedDefects?: string[];
  /** First candidate defect retained for legacy display compatibility. */
  discrepancy?: string;
  declaredDate?: string;
  declaredTimeZulu?: string;
  nextAdviseDate?: string;
  nextAdviseTimeZulu?: string;
  ertsDate?: string;
  ertsTimeZulu?: string;
  /** Explicit source instruction; blank ERTS data alone means no change. */
  ertsAction?: TriageErtsAction;
  /** True once the required 30-minute auto-ingestion buffer has been applied. */
  triageTimingBufferApplied?: boolean;
  /** Deferred-specific fields (ATN-Deferred feed); null for other feeds. */
  deferred?: TriageDeferredFields | null;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Result of a pending-triage fetch, flagging whether mock data was used. */
export interface PendingTriageResult {
  items: TriageItem[];
  isMock: boolean;
}

/**
 * Fetches PENDING_REVIEW triage items. Returns the live list when the API is
 * reachable, otherwise the mock list (with `isMock: true`).
 */
export async function fetchPendingTriage(): Promise<PendingTriageResult> {
  try {
    const response = await authedFetch('/triage?status=pending');
    if (response && response.ok) {
      const data: PendingTriageApiResponse = await response.json();
      const triageItems: TriageItem[] = data.triageItems || [];
      return { items: triageItems.map(normalizeTriageItem), isMock: false };
    }
    logger.warn('Triage API unavailable; using mock triage data', { status: response?.status });
    return { items: MOCK_TRIAGE.map(normalizeTriageItem), isMock: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('fetchPendingTriage failed; using mock triage data', { error: message });
    return { items: MOCK_TRIAGE.map(normalizeTriageItem), isMock: true };
  }
}

/** Body for a commit request. */
export interface CommitTriageBody {
  action: 'CREATE' | 'UPDATE';
  event: Partial<UnscheduledEvent>;
  version: number;
}

/** Successful commit response containing the canonical saved event. */
export interface CommitTriageSuccess {
  ok: true;
  event: UnscheduledEvent;
}

/** Failed commit response. */
export interface CommitTriageFailure {
  ok: false;
}

/** Result of a commit request. */
export type CommitTriageResult = CommitTriageSuccess | CommitTriageFailure;

/** Checks that a successful commit response contains a versioned canonical event. */
function isCommitTriageSuccess(value: unknown): value is CommitTriageSuccess {
  if (typeof value !== 'object' || value === null || !('ok' in value) || value.ok !== true) {
    return false;
  }
  if (!('event' in value) || typeof value.event !== 'object' || value.event === null) {
    return false;
  }
  const event: object = value.event;
  if (!('eventId' in event) || typeof event.eventId !== 'string' || !event.eventId.trim()) {
    return false;
  }
  const versionIsValid: boolean =
    'version' in event &&
    typeof event.version === 'number' &&
    Number.isInteger(event.version) &&
    event.version >= 0;
  const revisionIsValid: boolean =
    'revisionCounter' in event &&
    typeof event.revisionCounter === 'number' &&
    Number.isInteger(event.revisionCounter) &&
    event.revisionCounter >= 0;
  return versionIsValid || revisionIsValid;
}

/**
 * Commits a triage item: the server creates or updates the event and marks the
 * item COMMITTED. A successful response contains the canonical saved event,
 * including its server-assigned id and optimistic-lock version. A non-OK,
 * unreachable, or malformed response resolves to `{ ok: false }`. The triage
 * id is sent in the body because S3 object keys can contain path separators.
 */
export async function commitTriageItem(
  triageId: string,
  body: CommitTriageBody,
): Promise<CommitTriageResult> {
  try {
    const response = await authedFetch('/triage/commit', {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify({ triageId, ...body }),
    });
    if (response && response.ok) {
      const data: unknown = await response.json();
      if (isCommitTriageSuccess(data)) return data;
      logger.warn('Commit triage returned malformed success response', { triageId });
      return { ok: false };
    }
    logger.warn('Commit triage failed', { status: response?.status, triageId });
    return { ok: false };
  } catch (err: unknown) {
    const message: string = err instanceof Error ? err.message : 'Unknown error';
    logger.error('commitTriageItem failed', { error: message, triageId });
    return { ok: false };
  }
}

/**
 * Dismisses a triage item (no event written). Returns true on success. The id
 * is sent in the body (not the path) because triage ids are S3 object keys
 * containing slashes, which HTTP API path routing cannot carry.
 */
export async function dismissTriageItem(triageId: string, version: number): Promise<boolean> {
  try {
    const response = await authedFetch('/triage/dismiss', {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify({ triageId, version }),
    });
    if (response && response.ok) return true;
    logger.warn('Dismiss triage failed', { status: response?.status, triageId });
    return false;
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error';
    logger.error('dismissTriageItem failed', { error: message, triageId });
    return false;
  }
}

const TRIAGE_DISMISS_BATCH_SIZE: number = 5;

/** Dismisses pending triage items in bounded parallel batches. */
export async function dismissTriageItemsInBatches(items: TriageItem[]): Promise<Set<string>> {
  const dismissedIds: Set<string> = new Set<string>();
  for (let index: number = 0; index < items.length; index += TRIAGE_DISMISS_BATCH_SIZE) {
    const batch: TriageItem[] = items.slice(index, index + TRIAGE_DISMISS_BATCH_SIZE);
    try {
      const outcomes: boolean[] = await Promise.all(
        batch.map(
          (item: TriageItem): Promise<boolean> => dismissTriageItem(item.triage_id, item.version),
        ),
      );
      batch.forEach((item: TriageItem, batchIndex: number): void => {
        if (outcomes[batchIndex]) dismissedIds.add(item.triage_id);
      });
    } catch (error: unknown) {
      logger.error('Triage dismiss batch failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        batchSize: batch.length,
      });
    }
  }
  return dismissedIds;
}
